import { AlertDialog, Box, Button, Flex } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { t } from 'i18next';
import { IButton } from 'interfaces/i-buttons';
import { IIdentifiedComponent } from 'interfaces/i-components';
import { RADIX } from 'lib_ts/enums/radix-ui';
import React, { useEffect, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';
import { v4 } from 'uuid';

interface IProps {
  trigger?: React.ReactNode;
  maxWidth?: string;

  title: string;
  description?: string;
  content?: React.ReactNode;

  cancel?: IButton;
  action: IButton;

  typeToConfirm?: string;

  onClose?: () => void;
}

export const CommonConfirmationDialog = (
  props: IIdentifiedComponent & IProps
) => {
  const [open, setOpen] = useState(true);
  const [confirmationInput, setConfirmationInput] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    const id = v4();

    useMiscStore.getState().registerDialog({
      identifier: props.identifier,
      v4ID: id,
      open: true,
    });

    return () => {
      useMiscStore.getState().registerDialog({
        identifier: props.identifier,
        v4ID: id,
        open: false,
      });
    };
  }, [open]);

  return (
    <ErrorBoundary identifier={props.identifier}>
      <AlertDialog.Root
        open={open}
        onOpenChange={(v) => {
          setOpen(v);

          if (!v) {
            props.onClose?.();
          }
        }}
      >
        {props.trigger && (
          <AlertDialog.Trigger>{props.trigger}</AlertDialog.Trigger>
        )}
        <AlertDialog.Content
          maxWidth={props.maxWidth ?? RADIX.DIALOG.WIDTH.SM}
          aria-describedby={
            props.description
              ? t(props.description).toString()
              : 'no description'
          }
        >
          <AlertDialog.Title>{t(props.title)}</AlertDialog.Title>

          {props.description && (
            <AlertDialog.Description>
              {t(props.description)}
            </AlertDialog.Description>
          )}

          {props.content}

          {props.typeToConfirm && (
            <Box mt="4">
              <CommonTextInput
                id="confirmation-string"
                placeholder="Type to confirm"
                value={confirmationInput}
                onChange={(v) => setConfirmationInput(v || '')}
              />
            </Box>
          )}

          <Flex gap={RADIX.FLEX.GAP.SM} mt="4" justify="end">
            {!props.cancel?.invisible && (
              <AlertDialog.Cancel>
                <Button
                  title={props.cancel?.tooltip}
                  onClick={props.cancel?.onClick}
                  disabled={props.cancel?.disabled}
                  className={props.cancel?.className}
                  variant={
                    props.cancel?.variant ?? RADIX.BUTTON.VARIANT.SECONDARY
                  }
                  color={props.cancel?.color ?? RADIX.COLOR.NEUTRAL}
                >
                  {props.cancel?.icon}{' '}
                  {t(props.cancel?.label ?? 'common.cancel')}
                </Button>
              </AlertDialog.Cancel>
            )}

            {!props.action.invisible && (
              <AlertDialog.Action>
                <Button
                  title={props.action.tooltip}
                  onClick={props.action.onClick}
                  disabled={
                    props.action.disabled ||
                    (!!props.typeToConfirm &&
                      confirmationInput !== props.typeToConfirm)
                  }
                  className={props.action.className}
                  variant={props.action.variant ?? RADIX.BUTTON.VARIANT.PRIMARY}
                  color={props.action.color ?? RADIX.COLOR.WARNING}
                  loading={props.action.loading}
                >
                  {props.action.icon}{' '}
                  {t(props.action.label ?? 'common.confirm')}
                </Button>
              </AlertDialog.Action>
            )}
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </ErrorBoundary>
  );
};
